/**
 * @fileoverview Stripe Integration Hook - Single Product Checkout
 * @module useStripe
 * 
 * Business Context:
 * - Handles Stripe checkout session creation for Texas Property Tax Appeal Course
 * - Single product with fixed pricing via Stripe Price ID
 * - Manages loading states and error handling for payment flow
 * 
 * Technical Context:
 * - Integrates with backend payment API
 * - Handles redirect-based checkout flow
 * - Provides user feedback during payment processing
 */

import { useState } from 'react';
import { apiClient } from '../api/client';

export interface CreateCheckoutData {
  successUrl?: string;
  cancelUrl?: string;
  guestCheckout?: boolean;
}

export const useStripe = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckoutSession = async (data: CreateCheckoutData = {}): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      // Default URLs based on checkout type
      const defaultSuccessUrl = data.guestCheckout 
        ? `${window.location.origin}/check-email?session_id={CHECKOUT_SESSION_ID}`
        : `${window.location.origin}/payment/success`;
      const defaultCancelUrl = `${window.location.origin}/?payment=cancelled`;

      // Use appropriate payment method based on checkout type
      const response = data.guestCheckout 
        ? await apiClient.integration.payment.createGuestCheckoutSession({
            successUrl: data.successUrl || defaultSuccessUrl,
            cancelUrl: data.cancelUrl || defaultCancelUrl,
          })
        : await apiClient.integration.payment.createCheckoutSession({
            successUrl: data.successUrl || defaultSuccessUrl,
            cancelUrl: data.cancelUrl || defaultCancelUrl,
          });

      if (response.data.success && response.data.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.data.url;
      } else {
        throw new Error('Invalid checkout session response');
      }
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 
                          err.message || 
                          'Failed to create checkout session';
      const errorCode = err.response?.data?.error?.code;
      
      console.error('Stripe checkout error:', err);
      setError(errorMessage);
      
      // Re-throw so the calling component can handle it
      const checkoutError = new Error(errorMessage) as Error & { code?: string };
      checkoutError.code = errorCode;
      throw checkoutError;
      
    } finally {
      setLoading(false);
    }
  };

  const clearError = (): void => {
    setError(null);
  };

  return {
    createCheckoutSession,
    loading,
    error,
    clearError
  };
}; 